// client/src/coms/views/watch_together_parts/sessionQueue.js
import { voiceState } from '../../voip/voiceclient_parts/state';
import { canControl, electHost, isNewerRevision, nextRevision } from './watchSync';
import { WATCH_PERMS, hasPerm, permsForIdentity, validateWatchChange } from './watchPermissions';

export const WATCH_QUEUE_PACKET_TOPIC = 'pa-watch-queue';
const WATCH_QUEUE_PACKET_TYPE = 'pa_watch_queue_v1';
const REQUEST_THROTTLE_MS = 2500;

const listeners = new Set();
const playbackListeners = new Set();
// The room this client's Watch Together session runs over. A Watch Together activity binds its server
// relay (watchRelayRoom.js) here; packets and resets from any other room (the voice room) are then
// ignored, so being in a voice channel never mixes another session into this one.
let boundTransport = null;

const store = {
  serverId: '',
  revision: 0,
  queue: [],
  activeQueueId: '',
  playback: null,
  skipVote: {
    queueId: '',
    identities: [],
    updatedAt: 0,
  },
  updatedAt: 0,
  lastRequesterIdentity: '',
  lastRequestAt: 0,
  // Lamport ordering (wall clocks differ between participants).
  lastFrom: '',
  playbackRev: 0,
  playbackFrom: '',
  // Control: 'everyone' (default, previous behaviour) or 'host'.
  host: '',
  controlMode: 'everyone',
};

function safeControlMode(value) {
  return String(value || '').trim().toLowerCase() === 'host' ? 'host' : 'everyone';
}

function safeId(value) {
  return String(value || '').trim();
}

function safeNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function safeProvider(value) {
  const provider = safeId(value).toLowerCase();
  if (provider === 'spotify') return 'spotify';
  if (provider === 'misc') return 'misc';
  return 'youtube';
}

function safeYouTubeId(value) {
  const id = safeId(value);
  return /^[A-Za-z0-9_-]{11}$/.test(id) ? id : '';
}

function safeSpotifyType(value) {
  const type = safeId(value).toLowerCase();
  if (type === 'track' || type === 'album' || type === 'playlist' || type === 'episode' || type === 'show' || type === 'artist') return type;
  return '';
}

function safeSpotifyInfo(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const mediaType = safeSpotifyType(raw.mediaType || raw.mt || raw.type);
  const mediaId = safeId(raw.mediaId || raw.mid || raw.id);
  const mediaUri = safeId(raw.mediaUri || raw.mu || (mediaType && mediaId ? `spotify:${mediaType}:${mediaId}` : ''));
  if (!mediaType || !mediaId || !mediaUri) return null;
  return {
    mediaType,
    mediaId,
    mediaUri,
    shareUrl: `https://open.spotify.com/${mediaType}/${mediaId}`,
    thumbnailUrl: safeId(raw.thumbnailUrl || raw.th) || '',
  };
}

function normalizePlayback(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const provider = safeProvider(raw.provider || raw.pr);

  if (provider === 'spotify') {
    const info = safeSpotifyInfo(raw);
    if (!info) return null;
    return {
      provider,
      mediaId: info.mediaId,
      mediaType: info.mediaType,
      mediaUri: info.mediaUri,
      currentTime: Math.max(0, safeNumber(raw.currentTime != null ? raw.currentTime : raw.ct, 0)),
      playing: !!(raw.playing != null ? raw.playing : raw.ply),
      updatedAt: safeNumber(raw.updatedAt != null ? raw.updatedAt : raw.ts, Date.now()),
    };
  }

  if (provider === 'misc') {
    const mediaId = safeId(raw.mediaId || raw.mid || raw.mediaUri || raw.mu || raw.sourceUrl || raw.su);
    if (!mediaId) return null;
    return {
      provider: 'misc',
      mediaId,
      mediaType: safeId(raw.mediaType || raw.mt) || 'embed',
      mediaUri: safeId(raw.mediaUri || raw.mu || raw.sourceUrl || raw.su || raw.embedUrl || raw.eu),
      currentTime: Math.max(0, safeNumber(raw.currentTime != null ? raw.currentTime : raw.ct, 0)),
      playing: !!(raw.playing != null ? raw.playing : raw.ply),
      updatedAt: safeNumber(raw.updatedAt != null ? raw.updatedAt : raw.ts, Date.now()),
    };
  }

  const mediaId = safeYouTubeId(raw.mediaId || raw.videoId || raw.mid || raw.vid);
  if (!mediaId) return null;

  return {
    provider: 'youtube',
    mediaId,
    mediaType: 'video',
    mediaUri: '',
    currentTime: Math.max(0, safeNumber(raw.currentTime != null ? raw.currentTime : raw.ct, 0)),
    playing: !!(raw.playing != null ? raw.playing : raw.ply),
    updatedAt: safeNumber(raw.updatedAt != null ? raw.updatedAt : raw.ts, Date.now()),
  };
}

function normalizeSkipVote(raw) {
  if (!raw || typeof raw !== 'object') {
    return {
      queueId: '',
      identities: [],
      updatedAt: 0,
    };
  }

  const queueId = safeId(raw.queueId != null ? raw.queueId : raw.qid);
  const identitiesRaw = Array.isArray(raw.identities) ? raw.identities : Array.isArray(raw.ids) ? raw.ids : [];
  const identities = Array.from(new Set(identitiesRaw.map((value) => safeId(value)).filter(Boolean)));
  const updatedAt = safeNumber(raw.updatedAt != null ? raw.updatedAt : raw.ts, Date.now());

  return {
    queueId,
    identities,
    updatedAt,
  };
}

function materializePlayback(playback = store.playback) {
  const p = normalizePlayback(playback);
  if (!p) return null;

  if (!p.playing) {
    return {
      provider: p.provider,
      mediaId: p.mediaId,
      mediaType: p.mediaType,
      mediaUri: p.mediaUri,
      currentTime: Math.max(0, p.currentTime),
      playing: false,
      updatedAt: Date.now(),
    };
  }

  const elapsed = Math.max(0, (Date.now() - p.updatedAt) / 1000);
  return {
    provider: p.provider,
    mediaId: p.mediaId,
    mediaType: p.mediaType,
    mediaUri: p.mediaUri,
    currentTime: Math.max(0, p.currentTime + elapsed),
    playing: true,
    updatedAt: Date.now(),
  };
}

function minimalPlayback(playback) {
  const p = materializePlayback(playback);
  if (!p) return null;
  return {
    pr: p.provider,
    mid: p.mediaId,
    mt: p.mediaType,
    mu: p.mediaUri,
    ct: p.currentTime,
    ply: p.playing ? 1 : 0,
  };
}

function cloneState() {
  return {
    serverId: store.serverId,
    revision: store.revision,
    queue: store.queue.map((item) => ({ ...item })),
    activeQueueId: store.activeQueueId,
    playback: materializePlayback(store.playback),
    skipVote: {
      queueId: store.skipVote.queueId,
      identities: [...store.skipVote.identities],
      updatedAt: store.skipVote.updatedAt,
    },
    updatedAt: store.updatedAt,
    host: store.host,
    controlMode: store.controlMode,
  };
}

function emit() {
  const snapshot = cloneState();

  listeners.forEach((listener) => {
    try {
      listener(snapshot);
    } catch {}
  });

  try {
    window.__VOX_WATCH_QUEUE = snapshot;
  } catch {}
}

function emitPlayback(playback = store.playback) {
  const snapshot = materializePlayback(playback);
  if (!snapshot) return;

  const payload = {
    serverId: store.serverId,
    provider: snapshot.provider,
    mediaId: snapshot.mediaId,
    mediaType: snapshot.mediaType,
    mediaUri: snapshot.mediaUri,
    currentTime: snapshot.currentTime,
    playing: snapshot.playing,
    updatedAt: snapshot.updatedAt,
  };

  playbackListeners.forEach((listener) => {
    try {
      listener(payload);
    } catch {}
  });

  try {
    window.__VOX_WATCH_QUEUE_PLAYBACK = payload;
  } catch {}
}

function activeRoom(preferredRoom = null) {
  if (preferredRoom && typeof preferredRoom === 'object') return preferredRoom;
  return voiceState && voiceState.activeRoom ? voiceState.activeRoom : null;
}

function textEncode(value) {
  return new TextEncoder().encode(JSON.stringify(value));
}

function textDecode(payload) {
  return JSON.parse(new TextDecoder().decode(payload));
}

function youtubeThumbnailFor(mediaId) {
  return `https://i.ytimg.com/vi/${mediaId}/hqdefault.jpg`;
}

function youtubeShareUrlFor(mediaId) {
  return `https://www.youtube.com/watch?v=${mediaId}`;
}

function spotifyThumbnailFallback() {
  return 'data:image/svg+xml;utf8,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 800 450"%3E%3Crect width="800" height="450" fill="%23121212"/%3E%3Ccircle cx="400" cy="225" r="100" fill="%231db954"/%3E%3Cpath d="M353 188c53-15 111-9 166 17" stroke="%23121212" stroke-width="16" stroke-linecap="round" fill="none"/%3E%3Cpath d="M362 223c42-12 87-7 131 13" stroke="%23121212" stroke-width="14" stroke-linecap="round" fill="none"/%3E%3Cpath d="M372 256c31-8 61-5 92 9" stroke="%23121212" stroke-width="12" stroke-linecap="round" fill="none"/%3E%3Ctext x="400" y="355" text-anchor="middle" fill="%23ffffff" font-family="Arial, sans-serif" font-size="34" font-weight="700"%3ESpotify%3C/text%3E%3C/svg%3E';
}

function normalizeQueueEntry(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const provider = safeProvider(raw.provider || raw.pr);
  const id = safeId(raw.id || raw.i);
  const addedBy = safeId(raw.addedBy || raw.b);
  const addedByIdentity = safeId(raw.addedByIdentity || raw.bi);
  const addedAt = Number(raw.addedAt || raw.t);
  const title = safeId(raw.title || raw.ti);

  if (provider === 'spotify') {
    const info = safeSpotifyInfo(raw);
    if (!info) return null;

    return {
      id: id || `q_spotify_${info.mediaType}_${info.mediaId}`,
      provider: 'spotify',
      mediaId: info.mediaId,
      mediaType: info.mediaType,
      mediaUri: info.mediaUri,
      shareUrl: `https://open.spotify.com/${info.mediaType}/${info.mediaId}`,
      thumbnailUrl: info.thumbnailUrl || spotifyThumbnailFallback(),
      title,
      addedBy,
      addedByIdentity,
      addedAt: Number.isFinite(addedAt) ? addedAt : Date.now(),
    };
  }

  if (provider === 'misc') {
    const mediaId = safeId(raw.mediaId || raw.mid || raw.mediaUri || raw.mu || raw.sourceUrl || raw.su || raw.embedUrl || raw.eu);
    if (!mediaId) return null;

    return {
      id: id || `q_misc_${Date.now()}` ,
      provider: 'misc',
      mediaId,
      mediaType: safeId(raw.mediaType || raw.mt) || 'embed',
      mediaUri: safeId(raw.mediaUri || raw.mu || raw.sourceUrl || raw.su || raw.embedUrl || raw.eu),
      shareUrl: safeId(raw.shareUrl || raw.sh) || safeId(raw.sourceUrl || raw.su || raw.embedUrl || raw.eu),
      sourceUrl: safeId(raw.sourceUrl || raw.su || raw.mediaUri || raw.mu),
      embedUrl: safeId(raw.embedUrl || raw.eu || raw.sourceUrl || raw.su || raw.mediaUri || raw.mu),
      mediaKind: safeId(raw.mediaKind || raw.mk) || 'iframe',
      hostLabel: safeId(raw.hostLabel || raw.hl) || 'Misc',
      thumbnailUrl: safeId(raw.thumbnailUrl || raw.th) || spotifyThumbnailFallback(),
      title,
      addedBy,
      addedByIdentity,
      addedAt: Number.isFinite(addedAt) ? addedAt : Date.now(),
    };
  }

  const mediaId = safeYouTubeId(raw.mediaId || raw.videoId || raw.mid || raw.v);
  if (!mediaId) return null;

  return {
    id: id || `q_youtube_${mediaId}`,
    provider: 'youtube',
    mediaId,
    mediaType: 'video',
    mediaUri: '',
    shareUrl: youtubeShareUrlFor(mediaId),
    thumbnailUrl: safeId(raw.thumbnailUrl || raw.th) || youtubeThumbnailFor(mediaId),
    title,
    addedBy,
    addedByIdentity,
    addedAt: Number.isFinite(addedAt) ? addedAt : Date.now(),
  };
}

function normalizeQueue(rawQueue) {
  if (!Array.isArray(rawQueue)) return [];
  const out = [];
  const seen = new Set();

  for (const raw of rawQueue) {
    const item = normalizeQueueEntry(raw);
    if (!item) continue;
    if (seen.has(item.id)) continue;
    seen.add(item.id);
    out.push(item);
  }

  return out;
}

function minimalQueue(queue) {
  return normalizeQueue(queue).map((item) => ({
    i: item.id,
    pr: item.provider,
    mid: item.mediaId,
    mt: item.mediaType,
    mu: item.mediaUri,
    su: item.sourceUrl,
    eu: item.embedUrl,
    mk: item.mediaKind,
    hl: item.hostLabel,
    sh: item.shareUrl,
    th: item.thumbnailUrl,
    ti: item.title,
    b: item.addedBy,
    bi: item.addedByIdentity,
    t: item.addedAt,
  }));
}

function minimalSkipVote(skipVote) {
  const vote = normalizeSkipVote(skipVote);
  return {
    qid: vote.queueId,
    ids: vote.identities,
    ts: vote.updatedAt || Date.now(),
  };
}

function parsePacket(payload) {
  try {
    return parsePacketObject(textDecode(payload));
  } catch {
    return null;
  }
}

function parsePacketObject(raw) {
  try {
    if (!raw || raw.__pa !== WATCH_QUEUE_PACKET_TYPE) return null;

    const kind = safeId(raw.k).toLowerCase();
    if (!kind) return null;

    return {
      type: WATCH_QUEUE_PACKET_TYPE,
      kind,
      serverId: safeId(raw.sid),
      revision: Number(raw.rev) || 0,
      activeQueueId: safeId(raw.a),
      queue: normalizeQueue(raw.q),
      playback: normalizePlayback(raw.p || raw),
      skipVote: normalizeSkipVote(raw.sv),
      voteQueueId: safeId(raw.qid),
      voteIdentity: safeId(raw.vi),
      voteValue: raw.voted === 1 || raw.voted === true,
      ts: Number(raw.ts) || Date.now(),
      requesterIdentity: safeId(raw.ri),
      fromIdentity: safeId(raw.fi),
      host: safeId(raw.h),
      controlMode: safeControlMode(raw.cm),
      hasControl: raw.h != null || raw.cm != null,
      isReply: raw.rp === 1,
    };
  } catch {
    return null;
  }
}

async function publishPacket(packet, preferredRoom = null, destinationIdentities = undefined) {
  const room = activeRoom(preferredRoom);
  const localParticipant = room && room.localParticipant ? room.localParticipant : null;
  if (!localParticipant || typeof localParticipant.publishData !== 'function') return false;

  const bytes = textEncode(packet);

  try {
    const options = { reliable: true, topic: WATCH_QUEUE_PACKET_TOPIC };
    if (Array.isArray(destinationIdentities) && destinationIdentities.length) {
      options.destinationIdentities = destinationIdentities;
    }
    await localParticipant.publishData(bytes, options);
    return true;
  } catch {}

  try {
    const options = { reliable: true };
    if (Array.isArray(destinationIdentities) && destinationIdentities.length) {
      options.destinationIdentities = destinationIdentities;
    }
    await localParticipant.publishData(bytes, options);
    return true;
  } catch {}

  try {
    await localParticipant.publishData(bytes);
    return true;
  } catch {}

  return false;
}

function applyPlayback(playback, { emitNow = true } = {}) {
  const nextPlayback = normalizePlayback(playback);
  if (!nextPlayback) {
    store.playback = null;
    return false;
  }

  store.playback = {
    provider: nextPlayback.provider,
    mediaId: nextPlayback.mediaId,
    mediaType: nextPlayback.mediaType,
    mediaUri: nextPlayback.mediaUri,
    currentTime: nextPlayback.currentTime,
    playing: nextPlayback.playing,
    updatedAt: Number(nextPlayback.updatedAt) || Date.now(),
  };

  if (emitNow) emitPlayback(store.playback);
  return true;
}

function sanitizeSkipVoteAgainstQueue(skipVote, queue, activeQueueId) {
  const vote = normalizeSkipVote(skipVote);
  const activeId = safeId(activeQueueId);

  if (!vote.queueId) {
    return {
      queueId: '',
      identities: [],
      updatedAt: vote.updatedAt || Date.now(),
    };
  }

  if (!queue.some((item) => item.id === vote.queueId)) {
    return {
      queueId: '',
      identities: [],
      updatedAt: Date.now(),
    };
  }

  if (activeId && vote.queueId !== activeId) {
    return {
      queueId: '',
      identities: [],
      updatedAt: Date.now(),
    };
  }

  return {
    queueId: vote.queueId,
    identities: [...vote.identities],
    updatedAt: vote.updatedAt || Date.now(),
  };
}

function applySkipVoteUpdate({ queueId, voterIdentity, voted }) {
  const nextQueueId = safeId(queueId);
  const nextVoterIdentity = safeId(voterIdentity);
  if (!nextQueueId || !nextVoterIdentity) return false;
  if (!store.queue.length) return false;
  if (store.activeQueueId && nextQueueId !== store.activeQueueId) return false;
  if (!store.queue.some((item) => item.id === nextQueueId)) return false;

  const currentVote = sanitizeSkipVoteAgainstQueue(store.skipVote, store.queue, store.activeQueueId);
  const identities = currentVote.queueId === nextQueueId ? [...currentVote.identities] : [];
  const already = identities.includes(nextVoterIdentity);

  if (voted) {
    if (!already) identities.push(nextVoterIdentity);
  } else if (already) {
    const index = identities.indexOf(nextVoterIdentity);
    if (index >= 0) identities.splice(index, 1);
  }

  store.skipVote = {
    queueId: identities.length ? nextQueueId : '',
    identities,
    updatedAt: Date.now(),
  };

  emit();
  return true;
}

function applySnapshot(snapshot, force = false) {
  if (!snapshot) return false;

  const incomingServerId = safeId(snapshot.serverId);
  const incomingRevision = Number(snapshot.revision) || 0;

  if (!force && store.serverId && incomingServerId && incomingServerId !== store.serverId) return false;
  if (!force && !isNewerRevision({ rev: incomingRevision, from: snapshot.fromIdentity }, { rev: store.revision, from: store.lastFrom })) {
    return false;
  }

  const queue = normalizeQueue(snapshot.queue);
  let activeQueueId = safeId(snapshot.activeQueueId);

  if (activeQueueId && !queue.some((item) => item.id === activeQueueId)) {
    activeQueueId = '';
  }
  if (!activeQueueId && queue.length) activeQueueId = queue[0].id;

  store.serverId = incomingServerId || store.serverId;
  store.revision = incomingRevision || nextRevision(store.revision);
  store.lastFrom = safeId(snapshot.fromIdentity) || store.lastFrom;
  if (snapshot.hasControl !== false && (snapshot.host != null || snapshot.controlMode != null)) {
    if (snapshot.host != null) store.host = safeId(snapshot.host);
    if (snapshot.controlMode != null) store.controlMode = safeControlMode(snapshot.controlMode);
  }
  store.queue = queue;
  store.activeQueueId = activeQueueId;
  store.updatedAt = Date.now();

  if (snapshot.playback) {
    applyPlayback(snapshot.playback, { emitNow: false });
  }

  store.skipVote = sanitizeSkipVoteAgainstQueue(snapshot.skipVote, store.queue, store.activeQueueId);

  emit();

  if (snapshot.playback) {
    emitPlayback(store.playback);
  }

  return true;
}

function buildStatePacket({ serverId, queue, activeQueueId, playback, skipVote, revision, fromIdentity, host, controlMode, isReply = false }) {
  const nextQueue = normalizeQueue(queue);
  let nextActiveQueueId = safeId(activeQueueId);

  if (nextActiveQueueId && !nextQueue.some((item) => item.id === nextActiveQueueId)) {
    nextActiveQueueId = '';
  }
  if (!nextActiveQueueId && nextQueue.length) nextActiveQueueId = nextQueue[0].id;

  const nextSkipVote = sanitizeSkipVoteAgainstQueue(skipVote, nextQueue, nextActiveQueueId);

  return {
    __pa: WATCH_QUEUE_PACKET_TYPE,
    k: 'state',
    ...(isReply ? { rp: 1 } : {}),
    sid: safeId(serverId),
    rev: Number(revision) || Date.now(),
    a: nextActiveQueueId,
    q: minimalQueue(nextQueue),
    p: minimalPlayback(playback),
    sv: minimalSkipVote(nextSkipVote),
    ts: Date.now(),
    fi: safeId(fromIdentity),
    h: safeId(host),
    cm: safeControlMode(controlMode),
  };
}

export function getWatchQueueSnapshot() {
  return cloneState();
}

export function subscribeWatchQueue(listener) {
  if (typeof listener !== 'function') return () => {};
  listeners.add(listener);

  try {
    listener(cloneState());
  } catch {}

  return () => listeners.delete(listener);
}

export function subscribeWatchQueuePlayback(listener) {
  if (typeof listener !== 'function') return () => {};
  playbackListeners.add(listener);

  try {
    const playback = materializePlayback(store.playback);
    if (playback) {
      listener({
        serverId: store.serverId,
        provider: playback.provider,
        mediaId: playback.mediaId,
        mediaType: playback.mediaType,
        mediaUri: playback.mediaUri,
        currentTime: playback.currentTime,
        playing: playback.playing,
        updatedAt: playback.updatedAt,
      });
    }
  } catch {}

  return () => playbackListeners.delete(listener);
}

export function bindWatchTransport(room) {
  boundTransport = room && typeof room === 'object' ? room : null;
}

export function unbindWatchTransport(room) {
  if (boundTransport && boundTransport === room) boundTransport = null;
}

export function getBoundWatchTransport() {
  return boundTransport;
}

// The server's authoritative state (on joining, reconnecting, or after a refused packet). It replaces
// whatever this client had, including the playback timeline.
export function applyAuthoritativeWatchSnapshot(raw) {
  const packet = parsePacketObject(raw);
  if (!packet || packet.kind !== 'state') return false;
  if (!packet.playback) store.playback = null;
  applySnapshot(packet, true);
  store.revision = Number(packet.revision) || 0;
  store.playbackRev = Number(packet.revision) || 0;
  store.playbackFrom = packet.fromIdentity || '';
  return true;
}

// Host and control mode decided by the server (hand-over after a disconnect, transfer, settings).
export function applyWatchControl({ host, controlMode } = {}) {
  let changed = false;
  if (host != null && safeId(host) !== store.host) {
    store.host = safeId(host);
    changed = true;
  }
  if (controlMode != null && safeControlMode(controlMode) !== store.controlMode) {
    store.controlMode = safeControlMode(controlMode);
    changed = true;
  }
  if (changed) emit();
  return changed;
}

// fromTransport: the room asking. The voice room's teardown must not wipe a session bound to the relay.
export function resetWatchQueueState(fromTransport = null) {
  if (boundTransport && fromTransport !== boundTransport) return;
  store.serverId = '';
  store.revision = 0;
  store.queue = [];
  store.activeQueueId = '';
  store.playback = null;
  store.skipVote = {
    queueId: '',
    identities: [],
    updatedAt: 0,
  };
  store.updatedAt = 0;
  store.lastRequesterIdentity = '';
  store.lastRequestAt = 0;
  store.lastFrom = '';
  store.playbackRev = 0;
  store.playbackFrom = '';
  store.host = '';
  store.controlMode = 'everyone';
  emit();
}

function localIdentity(room) {
  return safeId(room && room.localParticipant && room.localParticipant.identity);
}

function presentIdentities(room) {
  const out = [];
  const local = localIdentity(room);
  if (local) out.push(local);
  try {
    if (room && room.remoteParticipants) room.remoteParticipants.forEach((p) => p && p.identity && out.push(String(p.identity)));
  } catch {}
  return out;
}

// The local participant's Watch Together rights (null = unknown / older server = allowed).
export function localWatchPerms(preferredRoom = null) {
  const room = activeRoom(preferredRoom);
  return permsForIdentity(room, localIdentity(room));
}

export function canLocalWatch(key, preferredRoom = null) {
  return hasPerm(localWatchPerms(preferredRoom), key);
}

export function canLocalControlWatch(preferredRoom = null) {
  const room = activeRoom(preferredRoom);
  const perms = permsForIdentity(room, localIdentity(room));
  const manager = !!perms && perms.has(WATCH_PERMS.manageSessions);
  const byMode = canControl({ mode: store.controlMode, hostIdentity: store.host, identity: localIdentity(room) }) || manager;
  return byMode && (manager || hasPerm(perms, WATCH_PERMS.control));
}

// Re-elect the host when it left the room. Every participant computes the same result, so nothing
// needs to be broadcast. Returns true when the host changed.
export function reconcileWatchHost(preferredRoom = null) {
  const room = activeRoom(preferredRoom);
  if (!room) return false;
  const next = electHost(store.host, presentIdentities(room));
  if (next === store.host) return false;
  store.host = next;
  emit();
  return true;
}

export async function publishWatchControlMode(mode, preferredRoom = null) {
  const room = activeRoom(preferredRoom);
  if (!canLocalControlWatch(room)) return false;
  store.controlMode = safeControlMode(mode);
  if (!store.host) store.host = localIdentity(room);
  return publishWatchQueueState({ serverId: store.serverId, queue: store.queue, activeQueueId: store.activeQueueId }, room);
}

export async function publishWatchQueueState(
  { serverId, queue, activeQueueId, playback = undefined, skipVote = undefined },
  preferredRoom = null,
  destinationIdentity = ''
) {
  const nextServerId = safeId(serverId) || store.serverId;
  const nextQueue = normalizeQueue(queue);
  let nextActiveQueueId = safeId(activeQueueId);

  if (nextActiveQueueId && !nextQueue.some((item) => item.id === nextActiveQueueId)) {
    nextActiveQueueId = '';
  }
  if (!nextActiveQueueId && nextQueue.length) nextActiveQueueId = nextQueue[0].id;

  const room0 = activeRoom(preferredRoom);
  const me = localIdentity(room0);
  // A reply to a late joiner re-sends the current state unchanged (same revision and author);
  // only real edits advance the revision.
  const isReply = !!destinationIdentity;
  const revision = isReply ? store.revision : nextRevision(Math.max(store.revision, store.playbackRev));
  if (!isReply && !store.host && me) store.host = me;
  const nextSkipVote = sanitizeSkipVoteAgainstQueue(
    skipVote === undefined ? store.skipVote : skipVote,
    nextQueue,
    nextActiveQueueId
  );

  if (!isReply) {
    applySnapshot({
      serverId: nextServerId,
      revision,
      fromIdentity: me,
      queue: nextQueue,
      activeQueueId: nextActiveQueueId,
      playback: playback || store.playback,
      skipVote: nextSkipVote,
      host: store.host,
      controlMode: store.controlMode,
    }, true);
  }

  const room = room0;
  const fromIdentity = isReply ? store.lastFrom || me : me;

  const packet = buildStatePacket({
    serverId: nextServerId,
    queue: nextQueue,
    activeQueueId: nextActiveQueueId,
    playback: playback || store.playback,
    skipVote: nextSkipVote,
    revision,
    fromIdentity,
    host: store.host,
    controlMode: store.controlMode,
    isReply,
  });

  return publishPacket(packet, room, destinationIdentity ? [destinationIdentity] : undefined);
}

export async function publishWatchQueuePlayback(
  { serverId, provider, mediaId, mediaType, mediaUri, currentTime, playing },
  preferredRoom = null,
  options = {}
) {
  const nextServerId = safeId(serverId) || store.serverId;
  const nextPlayback = normalizePlayback({
    provider,
    mediaId,
    mediaType,
    mediaUri,
    currentTime: safeNumber(currentTime, 0),
    playing: !!playing,
    updatedAt: Date.now(),
  });
  if (!nextPlayback) return false;

  const destinationIdentities = Array.isArray(options.destinationIdentities)
    ? options.destinationIdentities.map((value) => safeId(value)).filter(Boolean)
    : [];
  const emitLocally = options.emitLocally !== false;

  store.serverId = nextServerId || store.serverId;

  const room = activeRoom(preferredRoom);
  const fromIdentity = localIdentity(room);
  const rev = nextRevision(Math.max(store.playbackRev, store.revision));
  store.playbackRev = rev;
  store.playbackFrom = fromIdentity;
  // Receivers raise their queue revision to this value; so must we, or our next queue edit reuses it
  // and is discarded by everyone else as already seen.
  store.revision = Math.max(store.revision, rev);

  if (emitLocally) {
    applyPlayback(nextPlayback, { emitNow: true });
  }

  return publishPacket({
    __pa: WATCH_QUEUE_PACKET_TYPE,
    k: 'playback',
    sid: nextServerId,
    p: minimalPlayback(nextPlayback),
    rev,
    ts: Date.now(),
    fi: fromIdentity,
  }, room, destinationIdentities.length ? destinationIdentities : undefined);
}

export async function publishWatchQueueSkipVote(
  { serverId, queueId, voterIdentity, voted },
  preferredRoom = null
) {
  const nextServerId = safeId(serverId) || store.serverId;
  const nextQueueId = safeId(queueId);
  const nextVoterIdentity = safeId(voterIdentity);

  if (!nextQueueId || !nextVoterIdentity) return false;

  store.serverId = nextServerId || store.serverId;
  applySkipVoteUpdate({
    queueId: nextQueueId,
    voterIdentity: nextVoterIdentity,
    voted: !!voted,
  });

  const room = activeRoom(preferredRoom);
  const fromIdentity = safeId(room?.localParticipant?.identity);

  return publishPacket({
    __pa: WATCH_QUEUE_PACKET_TYPE,
    k: 'skip',
    sid: nextServerId,
    qid: nextQueueId,
    vi: nextVoterIdentity,
    voted: voted ? 1 : 0,
    ts: Date.now(),
    fi: fromIdentity,
  }, room);
}

export async function requestWatchQueueState(serverId, preferredRoom = null) {
  const nextServerId = safeId(serverId) || store.serverId;
  const now = Date.now();
  if (now - store.lastRequestAt < REQUEST_THROTTLE_MS) return false;
  store.lastRequestAt = now;

  const room = activeRoom(preferredRoom);
  const requesterIdentity = safeId(room?.localParticipant?.identity);

  return publishPacket({
    __pa: WATCH_QUEUE_PACKET_TYPE,
    k: 'req',
    sid: nextServerId,
    ts: now,
    ri: requesterIdentity,
  }, room);
}

export function applyIncomingWatchQueuePacket(payload, participant, room, topic) {
  const maybeTopic = safeId(topic);
  if (maybeTopic && maybeTopic !== WATCH_QUEUE_PACKET_TOPIC) return false;

  const packet = parsePacket(payload);
  if (!packet) return false;
  // Another session's packet (for example from the voice room while a relay session is open): consumed, ignored.
  if (boundTransport && room !== boundTransport) return true;

  // LiveKit tells us who actually sent the packet; self-reported identities are only a fallback.
  const sender = safeId(participant && participant.identity) || packet.fromIdentity;
  // The sender's Watch Together rights (server-signed participant attributes; see watchPermissions.js).
  // Manage sessions acts as host in host-only mode; unknown rights never grant that.
  const senderPerms = permsForIdentity(room, sender);
  const sessionManager = !!senderPerms && senderPerms.has(WATCH_PERMS.manageSessions);
  const senderMayControl = canControl({ mode: store.controlMode, hostIdentity: store.host, identity: sender }) || sessionManager;

  if (packet.kind === 'req') {
    const requesterIdentity = safeId(packet.requesterIdentity || participant?.identity);
    if (!requesterIdentity) return true;
    if (!store.queue.length) return true;
    if (packet.serverId && store.serverId && packet.serverId !== store.serverId) return true;

    const replyPlayback = materializePlayback(store.playback);

    publishWatchQueueState({
      serverId: store.serverId || packet.serverId,
      queue: store.queue,
      activeQueueId: store.activeQueueId,
      playback: replyPlayback,
      skipVote: store.skipVote,
    }, room, requesterIdentity).catch(() => {});

    return true;
  }

  if (packet.kind === 'state') {
    // In host mode only the host edits; anyone may still bootstrap a client that has no state yet
    // (that is how late joiners receive the queue).
    // Late joiners take the current state from a peer's reply (it holds other people's items, so it cannot
    // be checked against the sender's own rights). Everything else - including the first edit of a new
    // session - is checked.
    const bootstrapping = !store.revision && !store.queue.length;
    const trustedReply = bootstrapping && packet.isReply;
    if (!senderMayControl && !bootstrapping) return true;
    if (!trustedReply) {
      const verdict = validateWatchChange({
        prev: { queue: store.queue, activeQueueId: store.activeQueueId, host: store.host, controlMode: store.controlMode },
        next: { queue: packet.queue, activeQueueId: packet.activeQueueId, host: packet.hasControl ? packet.host : null, controlMode: packet.hasControl ? packet.controlMode : null },
        sender,
        perms: senderPerms,
      });
      if (!verdict.ok) return true; // not allowed for this sender: ignored by everyone
    }
    applySnapshot(packet, false);
    return true;
  }

  if (packet.kind === 'playback') {
    if (packet.serverId && store.serverId && packet.serverId !== store.serverId) return true;
    if (!senderMayControl) return true;
    if (!sessionManager && !hasPerm(senderPerms, WATCH_PERMS.control)) return true;
    const rev = Number(packet.revision) || 0;
    if (rev && !isNewerRevision({ rev, from: sender }, { rev: store.playbackRev, from: store.playbackFrom })) return true;
    if (rev) {
      store.playbackRev = rev;
      store.playbackFrom = sender;
      store.revision = Math.max(store.revision, rev);
    }
    store.serverId = packet.serverId || store.serverId;
    applyPlayback(packet.playback, { emitNow: true });
    return true;
  }

  if (packet.kind === 'skip') {
    if (packet.serverId && store.serverId && packet.serverId !== store.serverId) return true;
    store.serverId = packet.serverId || store.serverId;
    // A participant can only vote for themselves.
    const voter = safeId(participant && participant.identity) || packet.voteIdentity;
    applySkipVoteUpdate({
      queueId: packet.voteQueueId,
      voterIdentity: voter,
      voted: packet.voteValue,
    });
    return true;
  }

  return false;
}
